import React, { useEffect } from 'react';
import { HiOutlineX } from 'react-icons/hi';

const isBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

// A section is shown only if at least one of its rows (or table rows) has a value.
const hasContent = (section) => {
  if (section.table) return (section.table.rows || []).length > 0;
  return (section.rows || []).some(r => !isBlank(r.value));
};

// Compact labelled button for list-table action columns (View / Edit / Delete / Approve ...).
// `color` must be a hex value: it is suffixed with alpha for the border and background.
export function ActionButton({ icon, label, color = '#475569', onClick, title }) {
  return (
    <button
      type="button"
      title={title || label}
      onClick={e => { e.stopPropagation(); onClick(e); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
        borderRadius: '8px', border: `1px solid ${color}40`, background: `${color}12`, color,
        fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {icon}{label}
    </button>
  );
}

/**
 * Full "A to Z" details of a record, laid out as labelled field tables grouped by section.
 *
 * sections: [{ title, icon, wide, rows: [{ label, value, hint }] }]
 *        or [{ title, icon, wide, table: { columns: [...], rows: [[...], ...] } }]
 * Empty rows inside a visible section show "—" so every field name is listed.
 */
export default function RecordDetailsModal({ title, subtitle, badges = [], sections = [], actions, onClose, maxWidth = '1080px' }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const visible = sections.filter(hasContent);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--bg-primary)', borderRadius: '16px', width: '100%', maxWidth,
          maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(15,23,42,0.35)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{title}</h3>
            {subtitle && <div style={{ marginTop: '3px', fontSize: '0.88rem', color: 'var(--text-dim)', fontWeight: 600 }}>{subtitle}</div>}
            {badges.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                {badges.filter(Boolean).map((b, i) => (
                  <span key={i} style={{ background: b.bg, color: b.color, border: `1px solid ${b.color}55`, padding: '3px 10px', borderRadius: '999px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {b.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} title="Close (Esc)" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.4rem', flexShrink: 0, padding: 0 }}>
            <HiOutlineX />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: '16px', alignItems: 'start' }}>
            {visible.map(section => (
              <div key={section.title} style={{
                gridColumn: section.wide ? '1 / -1' : undefined,
                background: 'var(--bg-sidebar)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden'
              }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', background: 'var(--bg-card-hover)' }}>
                  {section.icon ? `${section.icon} ` : ''}{section.title}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  {section.table ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                      <thead>
                        <tr>
                          {section.table.columns.map(c => (
                            <th key={c} style={{ textAlign: 'left', padding: '8px 14px', fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, ri) => (
                          <tr key={ri} style={{ borderBottom: ri < section.table.rows.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                            {row.map((cell, ci) => (
                              <td key={ci} style={{ padding: '8px 14px', color: 'var(--text-primary)', verticalAlign: 'top' }}>{isBlank(cell) ? '—' : cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <tbody>
                        {section.rows.map((row, ri) => (
                          <tr key={row.label} style={{ borderBottom: ri < section.rows.length - 1 ? '1px solid var(--border-light)' : 'none', background: ri % 2 ? 'var(--bg-card-hover)' : 'transparent' }}>
                            <th style={{ textAlign: 'left', verticalAlign: 'top', padding: '8px 14px', width: '42%', fontWeight: 600, color: 'var(--text-dim)' }}>
                              {row.label}
                              {row.hint && <div style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-ghost)', marginTop: '1px' }}>{row.hint}</div>}
                            </th>
                            <td style={{ padding: '8px 14px', color: isBlank(row.value) ? 'var(--text-ghost)' : 'var(--text-primary)', fontWeight: isBlank(row.value) ? 400 : 600, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {isBlank(row.value) ? '—' : row.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-sidebar)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flexShrink: 0 }}>
          {actions}
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
